
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { addDays, addMonths, format, parse, isValid } from 'date-fns';
import { Calendar } from 'lucide-react';

const OvulationCalculator = () => {
  const [lastPeriod, setLastPeriod] = useState('');
  const [cycleLength, setCycleLength] = useState('28');
  const [lutealPhase, setLutealPhase] = useState('14');
  const [results, setResults] = useState<{
    ovulationDate?: string;
    fertileWindowStart?: string;
    fertileWindowEnd?: string;
    nextPeriodDate?: string;
    nextThreeCycles?: Array<{
      periodStart: string;
      ovulationDate: string;
      fertileStart: string;
      fertileEnd: string;
    }>;
    message?: string;
  }>({});

  const handleCalculate = () => {
    try {
      // Parse the last period date
      const lastPeriodDate = parse(lastPeriod, 'yyyy-MM-dd', new Date());
      
      if (!isValid(lastPeriodDate)) {
        setResults({ message: 'Please enter a valid last period date' });
        return;
      }
      
      // Convert to numbers
      const cycleLengthNum = parseInt(cycleLength, 10);
      const lutealPhaseNum = parseInt(lutealPhase, 10);
      
      // Validate inputs
      if (cycleLengthNum < 21 || cycleLengthNum > 35) {
        setResults({ message: 'Cycle length should be between 21 and 35 days' });
        return;
      }
      
      if (lutealPhaseNum < 10 || lutealPhaseNum > 16) {
        setResults({ message: 'Luteal phase should be between 10 and 16 days' });
        return;
      }
      
      // Calculate ovulation day (cycle length - luteal phase)
      const ovulationDay = cycleLengthNum - lutealPhaseNum;
      const ovulationDate = addDays(lastPeriodDate, ovulationDay - 1);
      
      // Calculate fertile window (typically 5 days before ovulation through day of ovulation)
      const fertileWindowStart = addDays(ovulationDate, -5);
      const fertileWindowEnd = ovulationDate;
      
      // Calculate next period
      const nextPeriodDate = addDays(lastPeriodDate, cycleLengthNum);

      // Calculate next three cycles
      const nextThreeCycles = [];
      let currentPeriodStart = lastPeriodDate;
      
      for (let i = 0; i < 3; i++) {
        // Next cycle period start
        currentPeriodStart = addDays(currentPeriodStart, cycleLengthNum);
        
        // Ovulation for this cycle
        const cycleOvulationDate = addDays(currentPeriodStart, ovulationDay - 1);
        
        // Fertile window for this cycle
        const cycleFertileStart = addDays(cycleOvulationDate, -5);
        const cycleFertileEnd = cycleOvulationDate;
        
        nextThreeCycles.push({
          periodStart: format(currentPeriodStart, 'MMMM d, yyyy'),
          ovulationDate: format(cycleOvulationDate, 'MMMM d, yyyy'),
          fertileStart: format(cycleFertileStart, 'MMMM d, yyyy'),
          fertileEnd: format(cycleFertileEnd, 'MMMM d, yyyy')
        });
      }
      
      setResults({
        ovulationDate: format(ovulationDate, 'MMMM d, yyyy'),
        fertileWindowStart: format(fertileWindowStart, 'MMMM d, yyyy'),
        fertileWindowEnd: format(fertileWindowEnd, 'MMMM d, yyyy'),
        nextPeriodDate: format(nextPeriodDate, 'MMMM d, yyyy'),
        nextThreeCycles: nextThreeCycles
      });
    } catch (error) {
      setResults({ message: 'Error calculating ovulation dates. Please check your inputs.' });
    }
  };

  // Generate options for cycle length
  const cycleLengthOptions = Array.from({ length: 15 }, (_, i) => (21 + i).toString());
  
  // Generate options for luteal phase
  const lutealPhaseOptions = Array.from({ length: 7 }, (_, i) => (10 + i).toString());

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Ovulation Calculator</h3>
            <p className="text-sm text-muted-foreground">
              Calculate your estimated ovulation date and fertile window based on your menstrual cycle.
            </p>
          </div>
          
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="lastPeriod">First Day of Last Period</Label>
              <div className="flex">
                <Input
                  id="lastPeriod"
                  type="date"
                  value={lastPeriod}
                  onChange={(e) => setLastPeriod(e.target.value)}
                  className="flex-1"
                />
                <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
                  <Calendar className="h-4 w-4" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter the first day of your most recent menstrual period.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="cycleLength">Average Cycle Length</Label>
              <Select 
                value={cycleLength} 
                onValueChange={setCycleLength}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select cycle length" />
                </SelectTrigger>
                <SelectContent>
                  {cycleLengthOptions.map(option => (
                    <SelectItem key={option} value={option}>
                      {option} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The average length of your menstrual cycle (from first day of one period to the first day of the next).
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="lutealPhase">Luteal Phase Length</Label>
              <Select 
                value={lutealPhase} 
                onValueChange={setLutealPhase}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select luteal phase length" />
                </SelectTrigger>
                <SelectContent>
                  {lutealPhaseOptions.map(option => (
                    <SelectItem key={option} value={option}>
                      {option} days
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                The luteal phase is the time between ovulation and the start of your next period (typically 12-14 days).
              </p>
            </div>
            
            <Button onClick={handleCalculate} className="mt-2">
              Calculate Ovulation
            </Button>
          </div>

          {results.message ? (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {results.message}
            </div>
          ) : results.ovulationDate ? (
            <div className="mt-6 space-y-4">
              <div className="p-4 border rounded-md">
                <h4 className="text-lg font-medium mb-4">Current Cycle</h4>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium">Estimated Ovulation Date:</p>
                    <p className="text-lg font-semibold text-primary">{results.ovulationDate}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Fertile Window:</p>
                    <p>{results.fertileWindowStart} to {results.fertileWindowEnd}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Next Period Starts:</p>
                    <p>{results.nextPeriodDate}</p>
                  </div>
                </div>
              </div>
              
              {results.nextThreeCycles && (
                <div className="p-4 border rounded-md">
                  <h4 className="text-lg font-medium mb-4">Future Cycles Forecast</h4>
                  <div className="space-y-4">
                    {results.nextThreeCycles.map((cycle, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-md">
                        <h5 className="font-medium mb-2">Cycle {idx + 1}</h5>
                        <div className="space-y-1 text-sm">
                          <p><span className="font-medium">Period Starts:</span> {cycle.periodStart}</p>
                          <p><span className="font-medium">Fertile Window:</span> {cycle.fertileStart} to {cycle.fertileEnd}</p>
                          <p><span className="font-medium">Ovulation Date:</span> {cycle.ovulationDate}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="text-sm text-muted-foreground">
                <p>Note: This calculator provides estimates based on average cycle patterns. Every woman's body is different, and many factors can affect ovulation. For more accurate tracking, consider using basal body temperature monitoring or ovulation predictor kits.</p>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default OvulationCalculator;
